/*
 * @bot-written
 *
 * WARNING AND NOTICE
 * Any access, download, storage, and/or use of this source code is subject to the terms and conditions of the
 * Full Software Licence as accepted by you before being granted access to this source code and other materials,
 * the terms of which can be accessed on the Codebots website at https://codebots.com/full-software-licence. Any
 * commercial use in contravention of the terms of the Full Software Licence may be pursued by Codebots through
 * licence termination and further legal action, and be required to indemnify Codebots for any loss or damage,
 * including interest and costs. You are deemed to have accepted the terms of the Full Software Licence on any
 * access, download, storage, and/or use of this source code.
 *
 * BOT WARNING
 * This file is bot-written.
 * Any changes out side of "protected regions" will be lost next time the bot makes any changes.
 */
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeFetchManyToManyFunc,
	makeJoinEqualsFunc,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import AgencyPlaylisterAgencyTagEntity from 'Models/Security/Acl/AgencyPlaylisterAgencyTagEntity';
import ApplicationUserAgencyTagEntity from 'Models/Security/Acl/ApplicationUserAgencyTagEntity';
import VisitorsAgencyTagEntity from 'Models/Security/Acl/VisitorsAgencyTagEntity';
import AgencyAdminAgencyTagEntity from 'Models/Security/Acl/AgencyAdminAgencyTagEntity';
import MustardManagementAgencyTagEntity from 'Models/Security/Acl/MustardManagementAgencyTagEntity';
import MustardAdminAgencyTagEntity from 'Models/Security/Acl/MustardAdminAgencyTagEntity';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';
// % protected region % [Add any further imports here] off begin
// % protected region % [Add any further imports here] end

export interface IAgencyTagEntityAttributes extends IModelAttributes {
	name: string;
	agencyOwnerId: string;

	playlistss: Array<
		| Models.AgencyTagsPlaylists
		| Models.IAgencyTagsPlaylistsAttributes
	>;
	trackAgencyTagss: Array<
		| Models.AgencyTagsTrackAgencyTags
		| Models.IAgencyTagsTrackAgencyTagsAttributes
	>;
	// % protected region % [Add any custom attributes to the interface here] off begin
	// % protected region % [Add any custom attributes to the interface here] end
}

// % protected region % [Customise your entity metadata here] off begin
@entity('AgencyTagEntity', 'Agency Tag')
// % protected region % [Customise your entity metadata here] end
export default class AgencyTagEntity extends Model
	implements IAgencyTagEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new AgencyPlaylisterAgencyTagEntity(),
		new ApplicationUserAgencyTagEntity(),
		new VisitorsAgencyTagEntity(),
		new AgencyAdminAgencyTagEntity(),
		new MustardManagementAgencyTagEntity(),
		new MustardAdminAgencyTagEntity(),
		// % protected region % [Add any further ACL entries here] off begin
		// % protected region % [Add any further ACL entries here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [
		// % protected region % [Add any custom create exclusions here] off begin
		// % protected region % [Add any custom create exclusions here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [
		// % protected region % [Add any custom update exclusions here] off begin
		// % protected region % [Add any custom update exclusions here] end
	];

	// % protected region % [Modify props to the crud options here for attribute 'Name'] off begin
	@observable
	@attribute<AgencyTagEntity, string>()
	@CRUD({
		name: 'Name',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public name: string;
	// % protected region % [Modify props to the crud options here for attribute 'Name'] end

	// % protected region % [Modify props to the crud options here for attribute 'Agency Owner Id'] off begin
	@Validators.Uuid()
	@observable
	@attribute<AgencyTagEntity, string>()
	@CRUD({
		name: 'Agency Owner Id',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public agencyOwnerId: string;
	// % protected region % [Modify props to the crud options here for attribute 'Agency Owner Id'] end

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Playlists'] off begin
		name: 'Playlists',
		displayType: 'reference-multicombobox',
		order: 30,
		isJoinEntity: true,
		referenceTypeFunc: () => Models.AgencyTagsPlaylists,
		optionEqualFunc: makeJoinEqualsFunc('playlistsId'),
		referenceResolveFunction: makeFetchManyToManyFunc({
			entityName: 'agencyTagEntity',
			oppositeEntityName: 'playlistEntity',
			relationName: 'agencyTags',
			relationOppositeName: 'playlists',
			entity: () => Models.AgencyTagEntity,
			joinEntity: () => Models.AgencyTagsPlaylists,
			oppositeEntity: () => Models.PlaylistEntity,
		}),
		// % protected region % [Modify props to the crud options here for reference 'Playlists'] end
	})
	public playlistss: Models.AgencyTagsPlaylists[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Track Agency Tags'] off begin
		name: 'Track Agency Tags',
		displayType: 'reference-multicombobox',
		order: 40,
		isJoinEntity: true,
		referenceTypeFunc: () => Models.AgencyTagsTrackAgencyTags,
		optionEqualFunc: makeJoinEqualsFunc('trackAgencyTagsId'),
		referenceResolveFunction: makeFetchManyToManyFunc({
			entityName: 'agencyTagEntity',
			oppositeEntityName: 'trackEntity',
			relationName: 'agencyTags',
			relationOppositeName: 'trackAgencyTags',
			entity: () => Models.AgencyTagEntity,
			joinEntity: () => Models.AgencyTagsTrackAgencyTags,
			oppositeEntity: () => Models.TrackEntity,
		}),
		// % protected region % [Modify props to the crud options here for reference 'Track Agency Tags'] end
	})
	public trackAgencyTagss: Models.AgencyTagsTrackAgencyTags[] = [];

	// % protected region % [Add any custom attributes to the model here] off begin
	// % protected region % [Add any custom attributes to the model here] end

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IAgencyTagEntityAttributes>) {
		// % protected region % [Add any extra constructor logic before calling super here] off begin
		// % protected region % [Add any extra constructor logic before calling super here] end

		super(attributes);

		// % protected region % [Add any extra constructor logic after calling super here] off begin
		// % protected region % [Add any extra constructor logic after calling super here] end
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IAgencyTagEntityAttributes>) {
		// % protected region % [Override assign attributes here] off begin
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.agencyOwnerId !== undefined) {
				this.agencyOwnerId = attributes.agencyOwnerId;
			}
			if (attributes.playlistss !== undefined && Array.isArray(attributes.playlistss)) {
				for (const model of attributes.playlistss) {
					if (model instanceof Models.AgencyTagsPlaylists) {
						this.playlistss.push(model);
					} else {
						this.playlistss.push(new Models.AgencyTagsPlaylists(model));
					}
				}
			}
			if (attributes.trackAgencyTagss !== undefined && Array.isArray(attributes.trackAgencyTagss)) {
				for (const model of attributes.trackAgencyTagss) {
					if (model instanceof Models.AgencyTagsTrackAgencyTags) {
						this.trackAgencyTagss.push(model);
					} else {
						this.trackAgencyTagss.push(new Models.AgencyTagsTrackAgencyTags(model));
					}
				}
			}
			// % protected region % [Override assign attributes here] end

			// % protected region % [Add any extra assign attributes logic here] off begin
			// % protected region % [Add any extra assign attributes logic here] end
		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	// % protected region % [Customize Default Expands here] off begin
	public defaultExpands = `
		playlistss {
			${Models.AgencyTagsPlaylists.getAttributes().join('\n')}
			playlists {
				${Models.PlaylistEntity.getAttributes().join('\n')}
			}
		}
		trackAgencyTagss {
			${Models.AgencyTagsTrackAgencyTags.getAttributes().join('\n')}
			trackAgencyTags {
				${Models.TrackEntity.getAttributes().join('\n')}
				${Models.TrackEntity.getFiles().map(f => f.name).join('\n')}
			}
		}
	`;
	// % protected region % [Customize Default Expands here] end

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// % protected region % [Customize Save From Crud here] off begin
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			playlistss: {},
			trackAgencyTagss: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
							'playlistss',
							'trackAgencyTagss',
						],
					},
				],
			},
		);
	}
	// % protected region % [Customize Save From Crud here] end

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		// % protected region % [Customise the display name for this entity] on begin
		return this.name;
		// % protected region % [Customise the display name for this entity] end
	}

	// % protected region % [Add any further custom model features here] off begin
	// % protected region % [Add any further custom model features here] end
}

// % protected region % [Modify the create and modified CRUD attributes here] off begin
/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(AgencyTagEntity.prototype, 'created');
CRUD(modifiedAttr)(AgencyTagEntity.prototype, 'modified');
// % protected region % [Modify the create and modified CRUD attributes here] end
